import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Clock } from "lucide-react";
import { WHATSAPP_URL } from "@/config/constants";
import { solutions } from "@/data/services";

export const SolutionsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="servicos"
      ref={ref}
      // Estrutura de Scroll Snap e ocupação total de tela para foco na seção
      className="min-h-screen snap-start py-16 md:py-32 bg-white flex flex-col justify-center"
      data-testid="solutions-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 md:mb-16"
        >
          {/* Subtítulo institucional: Tracking largo para elegância visual */}
          <span className="text-gold text-sm uppercase tracking-[0.3em] font-semibold mb-4 block">
            Nossas Soluções
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-darkblue leading-tight mb-6">
            Como podemos ajudar sua empresa
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto">
            Oferecemos um conjunto completo de serviços para transformar a
            gestão do seu negócio e alcançar resultados extraordinários.
          </p>
        </motion.div>

        {/* Grid de Serviços: Cards com baixa opacidade de fundo e borda dinâmica */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="group bg-slate-50 p-6 md:p-8 border border-transparent hover:border-gold card-hover cursor-pointer"
              data-testid={`solution-card-${index}`}
            >
              {/* Ícone: Moldura sólida com inversão de cores (darkblue/gold) no hover */}
              <div className="w-14 h-14 md:w-16 md:h-16 bg-darkblue flex items-center justify-center mb-6 group-hover:bg-gold transition-colors duration-300">
                <solution.icon className="w-7 h-7 md:w-8 md:h-8 text-gold group-hover:text-darkblue transition-colors duration-300" />
              </div>
              <h3 className="font-heading text-lg md:text-xl font-bold text-darkblue mb-3">
                {solution.title}
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                {solution.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA: Botão com tracking e preenchimento sólido para conversão imediata */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold inline-flex items-center justify-center gap-3 bg-gold text-darkblue font-semibold px-6 sm:px-8 py-4 hover:bg-gold/90 transition-colors duration-300"
            data-testid="solutions-cta-btn"
          >
            <Clock size={20} />
            Agende uma reunião
          </a>
        </motion.div>
      </div>
    </section>
  );
};
